import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Briefcase, Building2, MapPin, Clock, DollarSign, Calendar,
  CheckCircle, ExternalLink, Send, Loader, Star, Users, Eye, AlertCircle, X,
  MessageSquare, Mic, Zap, CreditCard, Play, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ShareButtons from '../components/ShareButtons';
import ShareButton from '../components/ShareButton';
import { VerifiedApplyPrompt, VerifiedJobBadge } from '../components/VerifiedApplyPrompt';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const jobTypeLabels = {
  internship: 'Internship',
  full_time: 'Full-time',
  contract: 'Contract'
};

const workModeLabels = {
  remote: 'Remote',
  hybrid: 'Hybrid',
  onsite: 'On-site'
};

const experienceLevelLabels = {
  fresher: 'Fresher',
  '1_3_years': '1-3 years',
  '3_5_years': '3-5 years',
  '5_plus_years': '5+ years'
};

const CodeArenaJobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [hasApplied, setHasApplied] = useState(false);

  const [notFound, setNotFound] = useState(false);

  const isLoggedIn = !!localStorage.getItem('codearena_token');
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    fetchJob();
    if (isLoggedIn) {
      checkApplicationStatus();
    }
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const res = await axios.get(`${API}/codearena/jobs/${jobId}`);
      setJob(res.data);
      setNotFound(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to load job');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkApplicationStatus = async () => {
    try {
      const res = await axios.get(`${API}/codearena/me/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const applications = res.data.applications || [];
      setHasApplied(applications.some(app => app.job_id === jobId));
    } catch (err) {
      console.error('Failed to check application status');
    }
  };

  const handleApply = async () => {
    if (!isLoggedIn) {
      toast.error('Please login to apply');
      navigate(`/codearena/auth?redirect=${encodeURIComponent(`/jobs/${jobId}`)}`);
      return;
    }

    setApplying(true);
    try {
      await axios.post(
        `${API}/codearena/jobs/${jobId}/apply`,
        { cover_letter: coverLetter },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Track internal apply submission (fire-and-forget)
      axios.post(`${API}/codearena/jobs/${jobId}/apply-click`, {
        click_type: 'internal',
        referrer: typeof window !== 'undefined' ? window.location.href : '',
      }).catch(() => {});
      toast.success('Application submitted successfully!');
      setHasApplied(true);
      setShowApplyModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  // ── Helpers for Apply CTAs ──
  const isValidExternalUrl = (raw) => {
    if (!raw || typeof raw !== 'string') return false;
    try {
      const u = new URL(raw.trim());
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch { return false; }
  };

  const externalApplyUrl = isValidExternalUrl(job?.external_apply_link) ? job.external_apply_link.trim() : null;
  const validCompanyUrl = isValidExternalUrl(job?.company_url) ? job.company_url.trim() : null;
  // Smart logic — external takes precedence; we never show both options
  const useExternalApply = !!externalApplyUrl;
  const useInternalApply = !useExternalApply && (job?.apply_via_codearena !== false);

  const handleExternalApplyClick = (e) => {
    // Gate external apply behind login: redirect to auth and auto-open after login
    if (!isLoggedIn) {
      if (e) e.preventDefault();
      const redirectPath = `/jobs/${jobId}?apply_external=1`;
      navigate(`/codearena/auth?redirect=${encodeURIComponent(redirectPath)}`);
      return;
    }
    // fire-and-forget tracking
    axios.post(`${API}/codearena/jobs/${jobId}/apply-click`, {
      click_type: 'external',
      referrer: typeof window !== 'undefined' ? window.location.href : '',
    }).catch(() => {});
  };

  // Auto-open external apply link if user just logged in via external apply gate
  useEffect(() => {
    if (!job || !isLoggedIn) return;
    if (searchParams.get('apply_external') !== '1') return;
    if (!externalApplyUrl) {
      toast.error('External apply link is not available for this job.');
      // Clean the query so we don't loop
      const next = new URLSearchParams(searchParams);
      next.delete('apply_external');
      setSearchParams(next, { replace: true });
      return;
    }
    // Track + open in new tab via anchor click (most reliable, works with popup blockers)
    axios.post(`${API}/codearena/jobs/${jobId}/apply-click`, {
      click_type: 'external',
      referrer: typeof window !== 'undefined' ? window.location.href : '',
    }).catch(() => {});
    try {
      const link = document.createElement('a');
      link.href = externalApplyUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Opening external application page...');
    } catch (e) {
      // Fallback: same-tab redirect
      window.location.href = externalApplyUrl;
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('apply_external');
    setSearchParams(next, { replace: true });
  }, [job, isLoggedIn, externalApplyUrl, jobId, searchParams, setSearchParams]);

  const handleInternalApplyOpen = () => {
    if (!isLoggedIn) {
      navigate(`/codearena/auth?redirect=${encodeURIComponent(`/jobs/${jobId}`)}`);
      return;
    }
    setShowApplyModal(true);
  };

  const formatSalary = (min, max, currency = 'INR') => {
    if (!min && !max) return 'Not disclosed';
    const fmt = (n) => n?.toLocaleString('en-IN');
    if (min && max) return `${currency} ${fmt(min)} - ${fmt(max)}`;
    if (min) return `${currency} ${fmt(min)}+`;
    return `Up to ${currency} ${fmt(max)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAFA' }}>
        <Loader className="animate-spin text-[#002FA7]" size={28} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#FAFAFA', fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="job-not-found">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ background: '#FEE2E2' }}>
            <AlertCircle size={28} className="text-red-600" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
            Job Not Found
          </h1>
          <p className="text-sm text-zinc-500 mb-6">
            The job you're looking for doesn't exist, has been removed, or the link may be incorrect. Please check with the person who shared the link.
          </p>
          <div className="flex gap-3 justify-center">
            <Link
              to="/codearena/jobs"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold tracking-wide text-white"
              style={{ background: '#002FA7', borderRadius: '2px' }}
              data-testid="browse-all-jobs-btn"
            >
              Browse All Jobs
              <ArrowLeft size={14} className="rotate-180" />
            </Link>
            <Link
              to="/codearena"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold tracking-wide"
              style={{ color: '#0A0A0A', border: '1px solid #E4E4E7', borderRadius: '2px' }}
            >
              Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  const isDeadlinePassed = job.application_deadline && new Date(job.application_deadline) < new Date();
  const isJobInactive = job.is_active === false || job.approval_status === 'rejected' || job.approval_status === 'closed' || job.status === 'closed';
  const isApplicationsClosed = isDeadlinePassed || isJobInactive;

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: '#E4E4E7' }}>
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 h-14">
            <Link 
              to="/codearena/jobs" 
              className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors text-sm font-medium"
              data-testid="back-to-jobs-link"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Jobs</span>
            </Link>
            <div className="w-px h-5 bg-zinc-200" />
            <h1 className="text-sm font-bold tracking-tight truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
              {job.title}
            </h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-6">
        {/* Job Header Card */}
        <div className="bg-white border p-6 mb-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
            <div className="flex gap-4">
              {/* Company Avatar */}
              <div className="w-14 h-14 shrink-0 flex items-center justify-center text-lg font-bold"
                style={{ background: '#F4F4F5', color: '#002FA7', borderRadius: '2px', fontFamily: "'Space Grotesk', sans-serif" }}>
                {job.company_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  {job.is_featured && (
                    <span className="text-[9px] font-bold tracking-[0.1em] uppercase px-1.5 py-0.5"
                      style={{ background: '#FFD600', color: '#0A0A0A', borderRadius: '1px' }}>
                      FEATURED
                    </span>
                  )}
                  <span className="text-[10px] font-bold tracking-[0.08em] uppercase px-2 py-0.5 border"
                    style={{ borderColor: '#E4E4E7', color: '#002FA7', background: '#002FA710', borderRadius: '1px' }}>
                    {jobTypeLabels[job.job_type]}
                  </span>
                </div>
                
                <h2 className="text-xl font-bold tracking-tight mb-1 flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }} data-testid="job-title">
                  {job.title}
                  <VerifiedJobBadge job={job} />
                </h2>
                
                <div className="flex items-center gap-1.5 mb-3" data-testid="job-company-block">
                  <Building2 size={16} className="text-[#002FA7]" />
                  {validCompanyUrl ? (
                    <a
                      href={validCompanyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base font-bold tracking-tight hover:underline inline-flex items-center gap-1"
                      style={{ color: '#0A0A0A', fontFamily: "'Space Grotesk', sans-serif" }}
                      data-testid="job-company-link"
                    >
                      {job.company_name}
                      <ExternalLink size={12} className="text-[#002FA7]" />
                    </a>
                  ) : (
                    <span
                      className="text-base font-bold tracking-tight"
                      style={{ color: '#0A0A0A', fontFamily: "'Space Grotesk', sans-serif" }}
                      data-testid="job-company-name"
                    >
                      {job.company_name}
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px]" style={{ color: '#A1A1AA' }}>
                  <span className="flex items-center gap-1">
                    <MapPin size={12} />
                    {job.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase size={12} />
                    {workModeLabels[job.work_mode]}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {experienceLevelLabels[job.experience_level]}
                  </span>
                  <span className="flex items-center gap-1 font-medium" style={{ color: '#52525B', fontFamily: "'IBM Plex Mono', monospace" }}>
                    <DollarSign size={12} />
                    {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 shrink-0">
              <ShareButton kind="job" id={jobId} variant="outline" size="md" testid="og-share-job-btn" />
              <ShareButtons
                title={job.title}
                description={job.description}
                url={`/jobs/${jobId}`}
                shareText={`${job.title} at ${job.company_name} | ${job.location} | ${jobTypeLabels[job.job_type]} | Check it out on EvalentX!`}
                type="job"
              />
              
              {hasApplied ? (
                <div className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold tracking-wide"
                  style={{ background: '#05966910', color: '#059669', borderRadius: '2px', border: '1px solid #05966930' }}
                  data-testid="applied-badge"
                >
                  <CheckCircle size={14} />
                  Applied
                </div>
              ) : isApplicationsClosed ? (
                <div className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold tracking-wide"
                  style={{ background: '#71717a14', color: '#52525B', borderRadius: '2px', border: '1px solid #E4E4E7' }}
                  data-testid="applications-closed-badge"
                >
                  <AlertCircle size={14} />
                  Applications Closed
                </div>
              ) : useExternalApply ? (
                isLoggedIn ? (
                  <a
                    href={externalApplyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleExternalApplyClick}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 text-white text-sm font-bold tracking-wide transition-colors"
                    style={{ background: '#002FA7', borderRadius: '2px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#00227A'}
                    onMouseLeave={e => e.currentTarget.style.background = '#002FA7'}
                    data-testid="apply-btn-top"
                  >
                    <ExternalLink size={14} />
                    Apply Now
                  </a>
                ) : (
                  <button
                    onClick={handleExternalApplyClick}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 text-white text-sm font-bold tracking-wide transition-colors"
                    style={{ background: '#002FA7', borderRadius: '2px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#00227A'}
                    onMouseLeave={e => e.currentTarget.style.background = '#002FA7'}
                    data-testid="apply-btn-top"
                  >
                    <ExternalLink size={14} />
                    Login to Apply
                  </button>
                )
              ) : useInternalApply ? (
                <button
                  onClick={handleInternalApplyOpen}
                  className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-bold tracking-wide transition-colors"
                  style={{ background: '#002FA7', borderRadius: '2px' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#00227A'}
                  onMouseLeave={e => e.currentTarget.style.background = '#002FA7'}
                  data-testid="apply-btn-top"
                >
                  <Send size={14} />
                  {isLoggedIn ? 'Apply Now' : 'Login to Apply'}
                </button>
              ) : (
                <div className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium"
                  style={{ background: '#71717a10', color: '#52525B', borderRadius: '2px', border: '1px solid #E4E4E7' }}
                  data-testid="apply-not-available">
                  <AlertCircle size={14} />
                  Apply via the company directly
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Description */}
            <div className="bg-white border p-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }} data-testid="job-description-section">
              <h3 className="text-sm font-bold tracking-tight mb-4 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                Job Description
              </h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#52525B', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {job.description}
              </p>
            </div>

            <VerifiedApplyPrompt job={job} />

            {/* Skills */}
            <div className="bg-white border p-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }} data-testid="job-skills-section">
              <h3 className="text-sm font-bold tracking-tight mb-4 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                Skills Required
              </h3>
              <div className="flex flex-wrap gap-2">
                {job.skills_required?.map((skill, idx) => (
                  <span 
                    key={idx}
                    className="text-[11px] font-medium px-2.5 py-1"
                    style={{ background: '#F4F4F5', color: '#52525B', borderRadius: '1px' }}
                    data-testid={`skill-tag-${idx}`}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Bottom Apply CTA banner */}
            <div
              className="border p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
              style={{ borderColor: '#E4E4E7', background: '#0A0A0A', color: '#fff', borderRadius: '2px' }}
              data-testid="apply-cta-bottom"
            >
              <div className="min-w-0">
                <p className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: '#A1A1AA' }}>Ready to apply?</p>
                <h4 className="text-base sm:text-lg font-bold tracking-tight mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {job.title} <span className="text-zinc-500">at {job.company_name}</span>
                </h4>
                <p className="text-xs mt-1.5" style={{ color: '#A1A1AA' }}>
                  {job.location} · {jobTypeLabels[job.job_type]} · {workModeLabels[job.work_mode]}
                  {job.application_deadline && (
                    <> · Apply by <span style={{ color: isDeadlinePassed ? '#FCA5A5' : '#FBBF24', fontFamily: "'IBM Plex Mono', monospace" }}>{new Date(job.application_deadline).toLocaleDateString()}</span></>
                  )}
                </p>
              </div>
              <div className="shrink-0">
                {hasApplied ? (
                  <div className="flex items-center gap-2 px-5 py-3 text-sm font-bold tracking-wide" style={{ background: '#05966920', color: '#34D399', borderRadius: '2px', border: '1px solid #05966950' }}>
                    <CheckCircle size={14} /> You've Applied
                  </div>
                ) : isApplicationsClosed ? (
                  <div className="flex items-center gap-2 px-5 py-3 text-sm font-bold tracking-wide" style={{ background: '#27272A', color: '#A1A1AA', borderRadius: '2px', border: '1px solid #3F3F46' }} data-testid="applications-closed-bottom">
                    <AlertCircle size={14} /> Applications Closed
                  </div>
                ) : useExternalApply ? (
                  isLoggedIn ? (
                    <a
                      href={externalApplyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleExternalApplyClick}
                      className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-wide"
                      style={{ background: '#FFD600', color: '#0A0A0A', borderRadius: '2px' }}
                      data-testid="apply-btn-bottom"
                    >
                      <ExternalLink size={14} />
                      Apply Now
                    </a>
                  ) : (
                    <button
                      onClick={handleExternalApplyClick}
                      className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-wide"
                      style={{ background: '#FFD600', color: '#0A0A0A', borderRadius: '2px' }}
                      data-testid="apply-btn-bottom"
                    >
                      <ExternalLink size={14} />
                      Login to Apply
                    </button>
                  )
                ) : useInternalApply ? (
                  <button
                    onClick={handleInternalApplyOpen}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-wide"
                    style={{ background: '#FFD600', color: '#0A0A0A', borderRadius: '2px' }}
                    data-testid="apply-btn-bottom"
                  >
                    <Send size={14} />
                    {isLoggedIn ? 'Apply Now' : 'Login to Apply'}
                  </button>
                ) : (
                  <div className="text-xs" style={{ color: '#A1A1AA' }}>Contact the company directly to apply.</div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Job Details */}
            <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }} data-testid="job-details-sidebar">
              <h3 className="text-xs font-bold tracking-[0.1em] uppercase mb-4" style={{ color: '#A1A1AA' }}>
                Job Details
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'TYPE', value: jobTypeLabels[job.job_type] },
                  { label: 'MODE', value: workModeLabels[job.work_mode] },
                  { label: 'EXPERIENCE', value: experienceLevelLabels[job.experience_level] },
                  { label: 'SALARY', value: formatSalary(job.salary_min, job.salary_max, job.salary_currency), mono: true },
                  { label: 'LOCATION', value: job.location },
                ].map((item, idx) => (
                  <div key={idx}>
                    <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-0.5" style={{ color: '#A1A1AA' }}>{item.label}</p>
                    <p className="text-sm font-medium" style={{ color: '#0A0A0A', fontFamily: item.mono ? "'IBM Plex Mono', monospace" : 'inherit' }}>
                      {item.value}
                    </p>
                  </div>
                ))}
                {job.application_deadline && (
                  <div>
                    <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-0.5" style={{ color: '#A1A1AA' }}>DEADLINE</p>
                    <p className="text-sm font-medium" style={{ 
                      color: isDeadlinePassed ? '#DC2626' : '#D97706',
                      fontFamily: "'IBM Plex Mono', monospace"
                    }}>
                      {new Date(job.application_deadline).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }} data-testid="job-stats">
              <h3 className="text-xs font-bold tracking-[0.1em] uppercase mb-4" style={{ color: '#A1A1AA' }}>
                Statistics
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <Users className="mx-auto mb-1.5 text-[#002FA7]" size={18} />
                  <p className="text-xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>
                    {job.applications_count || 0}
                  </p>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Applicants</p>
                </div>
                <div className="text-center p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <Eye className="mx-auto mb-1.5 text-[#059669]" size={18} />
                  <p className="text-xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>
                    {job.views_count || 0}
                  </p>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Views</p>
                </div>
              </div>
            </div>

            {/* Company Card */}
            <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }} data-testid="job-company-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center font-bold"
                  style={{ background: '#002FA7', color: '#fff', borderRadius: '2px', fontFamily: "'Space Grotesk', sans-serif" }}>
                  {job.company_name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  {validCompanyUrl ? (
                    <a
                      href={validCompanyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold hover:underline inline-flex items-center gap-1"
                      style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}
                      data-testid="job-company-card-link"
                    >
                      <span className="truncate">{job.company_name}</span>
                      <ExternalLink size={11} className="text-[#002FA7] shrink-0" />
                    </a>
                  ) : (
                    <h3 className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>{job.company_name}</h3>
                  )}
                  <p className="text-[10px] font-medium" style={{ color: '#A1A1AA' }}>Hiring on EvalentX Arena</p>
                </div>
              </div>
            </div>

            {/* AI Mock Interview — Purchase & Practice */}
            <MockInterviewWidget job={job} isLoggedIn={isLoggedIn} token={token} navigate={navigate} />
          </div>
        </div>
      </div>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="apply-modal">
          <div className="bg-white border max-w-lg w-full" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  Apply for {job.title}
                </h2>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1AA' }}>at {job.company_name}</p>
              </div>
              <button 
                onClick={() => setShowApplyModal(false)}
                className="p-2 hover:bg-zinc-50 transition-colors"
                style={{ borderRadius: '2px' }}
              >
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            
            <div className="p-5">
              <div className="mb-5">
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>
                  Cover Letter (Optional)
                </label>
                <textarea
                  value={coverLetter}
                  onChange={(e) => setCoverLetter(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-3 text-sm border bg-white focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] resize-none"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  placeholder="Tell the company why you're a great fit for this role..."
                  data-testid="cover-letter-input"
                />
              </div>
              
              <div className="p-3 border mb-5" style={{ borderColor: '#002FA720', background: '#002FA708', borderRadius: '2px' }}>
                <p className="text-xs" style={{ color: '#002FA7' }}>
                  Your EvalentX Arena profile (including your score, rank, projects, and achievements) will be shared with the employer.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border transition-colors hover:bg-zinc-50"
                  style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}
                  data-testid="cancel-apply-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={applying}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-bold tracking-wide disabled:opacity-50 transition-colors"
                  style={{ background: '#002FA7', borderRadius: '2px' }}
                  data-testid="submit-application-btn"
                >
                  {applying ? (
                    <Loader className="animate-spin" size={16} />
                  ) : (
                    <>
                      <Send size={14} />
                      Submit Application
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Inline Mock Interview Purchase & Start Widget ───
const MockInterviewWidget = ({ job, isLoggedIn, token, navigate }) => {
  const [credits, setCredits] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [starting, setStarting] = useState(false);

  const fetchCredits = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoadingCredits(true);
    try {
      const res = await axios.get(`${API}/mock-interview/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredits(res.data);
    } catch { setCredits({ credits_remaining: 0 }); }
    finally { setLoadingCredits(false); }
  }, [isLoggedIn, token]);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const res = await axios.get(`${API}/mock-interview/plans`);
      setPlans(res.data.plans || []);
      if (!res.data.plans || res.data.plans.length === 0) {
        setPlansError('No plans available right now. Please try again shortly.');
      }
    } catch (e) {
      setPlansError('Could not load plans. Please refresh and try again.');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => { fetchCredits(); fetchPlans(); }, [fetchCredits, fetchPlans]);

  const purchasePlan = async (planId) => {
    if (!isLoggedIn) { navigate('/codearena/auth'); return; }
    setPurchasing(planId);
    try {
      // Read student profile from localStorage (matches /codearena/mock-interview dashboard contract)
      let student = {};
      try { student = JSON.parse(localStorage.getItem('codearena_student') || '{}'); } catch { student = {}; }
      const res = await axios.post(`${API}/mock-interview/purchase-plan`, {
        plan_id: planId,
        customer_name: student.full_name || student.name || 'Student',
        customer_email: student.email || '',
        customer_phone: student.phone || '9999999999',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { payment_session_id, order_id } = res.data;
      if (payment_session_id) {
        try {
          const { load } = await import('@cashfreepayments/cashfree-js');
          const cashfree = await load({ mode: 'production' });
          await cashfree.checkout({
            paymentSessionId: payment_session_id,
            returnUrl: `${window.location.origin}/codearena/mock-interview/payment-callback?order_id=${order_id}&return_to=${encodeURIComponent(window.location.pathname)}`
          });
        } catch (sdkErr) {
          console.error('Cashfree SDK error:', sdkErr);
          toast.error('Payment gateway failed to load. Please try again.');
        }
      } else {
        toast.success('Credits added!');
        fetchCredits();
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 422) {
        // Field validation error from backend — surface a friendly message
        toast.error('Could not start payment. Please update your profile email/phone and try again.');
      } else {
        toast.error(detail || 'Payment failed. Please try again.');
      }
    } finally { setPurchasing(null); }
  };

  const startInterview = async () => {
    if (!isLoggedIn) { navigate(loginUrlWithRedirect('/codearena/auth')); return; }
    if (!credits || credits.credits_remaining <= 0) {
      setShowPlans(true);
      toast.error('You need credits to start a mock interview. Pick a plan below.');
      return;
    }
    setStarting(true);
    try {
      const res = await axios.post(`${API}/mock-interview/sessions`, {
        job_description: `${job.title} at ${job.company_name}\n\n${job.description}${job.skills_required?.length ? `\n\nSkills Required: ${job.skills_required.join(', ')}` : ''}`,
        interview_type: 'mixed',
        job_title: job.title,
        company_name: job.company_name,
        duration_minutes: credits?.max_duration_minutes || 10,
        mode: 'voice'
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Backend returns { session: { id, ... }, initial_message: {...} }
      const sessionId = res.data?.session?.id || res.data?.session_id;
      if (sessionId) {
        toast.success('Interview ready!');
        navigate(`/codearena/mock-interview/${sessionId}`);
      } else {
        console.error('Unexpected interview response:', res.data);
        toast.error('Could not start the interview. Please try again.');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || 'Failed to start interview';
      if (status === 401) {
        toast.error('Please log in again to continue.');
        navigate('/codearena/auth');
      } else if (status === 402 || /credits?/i.test(msg)) {
        // Refresh credits, surface plans, prompt user to buy
        fetchCredits();
        setShowPlans(true);
        toast.error('No credits remaining. Pick a plan below to continue.');
      } else {
        toast.error(msg);
      }
    } finally { setStarting(false); }
  };

  const hasCredits = credits && credits.credits_remaining > 0;

  return (
    <div className="bg-white border-2 overflow-hidden" style={{ borderColor: '#002FA7', borderRadius: '2px' }} data-testid="mock-interview-widget">
      {/* Header */}
      <div style={{ background: '#002FA7' }} className="px-5 py-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={16} className="text-[#FFD600]" />
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Athena AI Mock Interview
          </h3>
        </div>
        <p className="text-[11px] text-white/70">Practice for this exact role with AI</p>
      </div>

      <div className="p-5">
        {/* Loading credits placeholder (prevents blank-looking widget) */}
        {isLoggedIn && credits === null && (
          <div className="flex items-center justify-center gap-2 py-4 mb-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px', background: '#FAFAFA' }} data-testid="credits-loading">
            <Loader className="animate-spin text-[#002FA7]" size={14} />
            <span className="text-xs font-medium" style={{ color: '#52525B' }}>Checking credits…</span>
          </div>
        )}

        {/* Credit Balance */}
        {isLoggedIn && credits !== null && (
          <div className="flex items-center justify-between p-3 mb-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px', background: hasCredits ? '#05966908' : '#F4F4F5' }}>
            <div className="flex items-center gap-2">
              <CreditCard size={14} style={{ color: hasCredits ? '#059669' : '#A1A1AA' }} />
              <span className="text-xs font-medium" style={{ color: '#52525B' }}>Your Credits</span>
            </div>
            <span className="text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: hasCredits ? '#059669' : '#DC2626' }}>
              {loadingCredits ? '...' : credits.credits_remaining}
            </span>
          </div>
        )}

        {/* No-credits explainer — never leaves the page blank */}
        {isLoggedIn && credits !== null && !hasCredits && !showPlans && (
          <div className="p-3 mb-3 border flex items-start gap-2" style={{ borderColor: '#FBBF24', borderRadius: '2px', background: '#FFFBEB' }} data-testid="no-credits-msg">
            <AlertCircle size={14} className="text-[#D97706] flex-shrink-0 mt-0.5" />
            <p className="text-[12px] leading-snug" style={{ color: '#78350F' }}>
              You need credits to start a mock interview. Pick a plan below to get started.
            </p>
          </div>
        )}

        {/* Start Button (if credits available) */}
        {hasCredits && (
          <button
            onClick={startInterview}
            disabled={starting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-bold tracking-wide transition-all mb-3 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: '#059669', borderRadius: '2px' }}
            data-testid="start-interview-btn"
          >
            {starting ? <Loader className="animate-spin" size={16} /> : <Play size={16} />}
            {starting ? 'Starting…' : `Start Interview for "${job.title}"`}
          </button>
        )}

        {/* Buy Prompt (if no credits or not logged in) */}
        {(!isLoggedIn || (credits !== null && !hasCredits)) && !showPlans && (
          <button
            onClick={() => isLoggedIn ? setShowPlans(true) : navigate('/codearena/auth')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-bold tracking-wide transition-colors"
            style={{ background: '#002FA7', borderRadius: '2px' }}
            data-testid="buy-credits-btn"
          >
            <Zap size={16} />
            {isLoggedIn ? 'Buy Credits & Start Mock Interview' : 'Login to Practice'}
          </button>
        )}

        {/* Inline Plans */}
        {showPlans && (
          <div className="space-y-2" data-testid="interview-plans">
            <p className="text-xs font-bold uppercase tracking-[0.1em] mb-3" style={{ color: '#A1A1AA' }}>Choose a Plan</p>

            {plansLoading && (
              <div className="flex items-center justify-center py-6 gap-2" data-testid="plans-loading">
                <Loader className="animate-spin text-[#002FA7]" size={16} />
                <span className="text-xs font-medium" style={{ color: '#52525B' }}>Loading plans…</span>
              </div>
            )}

            {!plansLoading && plans.length === 0 && (
              <div className="p-4 border text-center" style={{ borderColor: '#E4E4E7', borderRadius: '2px', background: '#FAFAFA' }} data-testid="plans-empty">
                <AlertCircle className="mx-auto text-[#A1A1AA] mb-2" size={20} />
                <p className="text-xs font-medium" style={{ color: '#52525B' }}>
                  {plansError || 'No plans available right now.'}
                </p>
                <button
                  onClick={fetchPlans}
                  className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold border transition-colors hover:border-[#002FA7] hover:text-[#002FA7]"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#52525B' }}
                  data-testid="plans-retry"
                >
                  Retry
                </button>
              </div>
            )}

            {!plansLoading && plans.map(plan => (
              <div
                key={plan.id}
                className="relative p-3 border transition-all hover:border-[#002FA7] cursor-pointer group"
                style={{ borderColor: plan.popular ? '#002FA7' : '#E4E4E7', borderRadius: '2px', background: plan.popular ? '#002FA708' : '#fff' }}
                onClick={() => purchasePlan(plan.id)}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <span className="absolute -top-2 right-3 text-[9px] font-bold tracking-[0.1em] uppercase px-2 py-0.5"
                    style={{ background: '#FFD600', color: '#0A0A0A', borderRadius: '1px' }}>
                    Popular
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#0A0A0A', fontFamily: "'Space Grotesk', sans-serif" }}>{plan.name}</p>
                    <p className="text-[11px]" style={{ color: '#A1A1AA' }}>{plan.interviews} interview{plan.interviews > 1 ? 's' : ''} · {plan.duration_minutes} min each</p>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <span className="text-base font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>
                      ₹{plan.price}
                    </span>
                    {purchasing === plan.id ? (
                      <Loader className="animate-spin text-[#002FA7]" size={16} />
                    ) : (
                      <span className="text-[10px] font-bold px-2 py-1 text-[#002FA7] border border-[#002FA7]/30 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                        BUY
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {hasCredits && (
              <button
                onClick={() => setShowPlans(false)}
                className="w-full text-xs text-[#A1A1AA] hover:text-[#52525B] py-2 transition-colors"
              >
                Already have credits? Go back
              </button>
            )}
          </div>
        )}

        {/* Value Prop */}
        {!showPlans && (
          <div className="mt-4 pt-3 border-t space-y-2" style={{ borderColor: '#E4E4E7' }}>
            {[
              { icon: Mic, text: 'Voice-based AI interview' },
              { icon: Star, text: 'Scored on communication & technical depth' },
              { icon: MessageSquare, text: 'Question-by-question feedback' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon size={12} className="text-[#002FA7] flex-shrink-0" />
                <span className="text-[11px]" style={{ color: '#52525B' }}>{item.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeArenaJobDetailPage;
